import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Circle from '@/v1/components/atoms/Circle';
import Text from '@/v1/components/atoms/Text';
import Battery from '@/v1/components/atoms/Icon/Battery';
import VoiceAssistant from '@/v1/components/atoms/Icon/VoiceAssistant';
import AlexaSpeechBubble from '@/v1/components/atoms/Icon/AlexaSpeechBubble';
import Switch from '@/v1/components/atoms/Switch';
import Button from '@/v1/components/atoms/Button';

import './Dialog.scss';

const ENTER_ANIMATION_NAME = 'enter';
const LEAVE_ANIMATION_NAME = 'leave';
const BATTERY_ANIMATION_TIME = 1360;

const propTypes = {
    buttonText: PropTypes.string,
    chatIconText: PropTypes.string,
    chatIcon: PropTypes.node,
    chatResponse: PropTypes.bool,
    headerTitle: PropTypes.string,
    headerTitleIcon: PropTypes.node,
    headerRightText: PropTypes.string,
    bodyTextHeader: PropTypes.node,
    bodyText: PropTypes.string,
    bodyTextIcon: PropTypes.node,
    bodyTextAfter: PropTypes.string,
    footerText: PropTypes.string,
    footerTextIcon: PropTypes.node,
    footerTextIconBlink: PropTypes.bool,
    switchLabelBefore: PropTypes.string,
    switchLabelAfter: PropTypes.string,
    onButtonClick: PropTypes.func,
    show: PropTypes.bool,
    variant: PropTypes.oneOf([null, 'bubble', 'app', 'alexa', 'charging']),
    className: PropTypes.string,
};
const defaultProps = {
    buttonText: null,
    chatIconText: null,
    chatIcon: null,
    chatResponse: false,
    headerTitle: null,
    headerTitleIcon: null,
    headerRightText: null,
    bodyTextHeader: null,
    bodyText: null,
    bodyTextIcon: null,
    bodyTextAfter: null,
    footerText: null,
    footerTextIcon: null,
    footerTextIconBlink: false,
    switchLabelBefore: 'Insert label',
    switchLabelAfter: 'Insert label',
    onButtonClick: null,
    variant: null,
    show: true,
    className: null,
};

const Dialog = ({
    buttonText,
    chatIconText,
    chatIcon,
    chatResponse,
    headerTitle,
    headerTitleIcon,
    headerRightText,
    bodyTextHeader,
    bodyText,
    bodyTextIcon,
    bodyTextAfter,
    footerText,
    footerTextIcon,
    footerTextIconBlink,
    switchLabelBefore,
    switchLabelAfter,
    onButtonClick,
    variant,
    show,
    className,
    ...props
}) => {
    const [battery, setBattery] = useState(false);
    const [batteryText, setBatteryText] = useState(false);
    const [showBatteryText, setShowBatteryText] = useState(null);
    const [animation, setAnimation] = useState(null);

    const dialog = React.useRef();

    const classes = classNames({
        Dialog: true,
        [`Dialog--${variant}`]: variant !== null,
        [className]: !!className,
    });

    const labelClasses = classNames({
        'fade-out': showBatteryText === false,
        'fade-in': showBatteryText,
    });

    const handleSwitchChange = () => {
        setBattery(true);
        setShowBatteryText(false);
        setTimeout(() => {
            setShowBatteryText(true);
            setBatteryText(true);
        }, BATTERY_ANIMATION_TIME);
        onButtonClick();
    };

    const onDialogButtonClick = () => {
        setAnimation(LEAVE_ANIMATION_NAME);
        dialog.current.addEventListener('animationend', onButtonClick);
    };

    useEffect(() => {
        const animationName = show ? ENTER_ANIMATION_NAME : LEAVE_ANIMATION_NAME;
        setAnimation(animationName);

        return () => {
            dialog.current.removeEventListener('animationend', onButtonClick);
        };
    }, [show]);

    return (
        <div
            ref={dialog}
            className={`Dialog__wrapper Dialog__wrapper--${variant} Dialog__wrapper--animate-${animation || ''}`}
            role="dialog"
            aria-labelledby="Dialog-title"
            aria-describedby="Dialog-desc"
        >
            <div className={classes} {...props}>
                {(variant === 'app' || variant === 'alexa') && !chatResponse && (
                    <Circle>
                        <Text component="span" className="label bold">
                            {chatIconText}
                        </Text>
                    </Circle>
                )}
                <div className="Dialog__content">
                    {headerTitle && (
                        <div className="Dialog__header-section">
                            <div className="Dialog__header">
                                {headerTitle && (
                                    <Text id="Dialog-title" component="h5" className="light" icon={headerTitleIcon}>
                                        {headerTitle}
                                    </Text>
                                )}
                                {headerRightText && variant !== 'charging' && (
                                    <div className="Dialog__header-right">
                                        <Text className="h5 light">{headerRightText}</Text>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    <div className="Dialog__body">
                        {bodyTextHeader && (
                            <div className="Dialog__body-header">
                                <Text component="h5" id="Dialog-desc">
                                    {bodyTextHeader}
                                </Text>
                                {variant === 'charging' && (
                                    <div className="Dialog__header-right">
                                        <Battery animate={battery} />
                                    </div>
                                )}
                            </div>
                        )}

                        {bodyText && (
                            <>
                                <div className="Dialog__body-content">
                                    {variant === 'app' || variant === 'alexa' ? (
                                        <Text className="p dialog light">{bodyText}</Text>
                                    ) : (
                                        <Text className="h5 light">
                                            <>
                                                {bodyTextIcon && <span className="Dialog__body-icon">{bodyTextIcon}</span>}
                                                {!batteryText ? bodyText : bodyTextAfter}
                                            </>
                                        </Text>
                                    )}
                                    {variant === 'charging' && (
                                        <Switch
                                            labelClasses={labelClasses}
                                            onChange={handleSwitchChange}
                                            label={batteryText ? switchLabelAfter : switchLabelBefore}
                                        />
                                    )}
                                </div>
                                {footerText && (
                                    <div className="Dialog__bottom">
                                        <span className={`Dialog__bottom-icon ${footerTextIconBlink ? 'blink' : ''}`}>{footerTextIcon}</span>
                                        <Text className="Dialog__bottom-text h5 light">{footerText}</Text>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
                {variant === 'app' && chatResponse && (
                    <Circle>
                        <VoiceAssistant className="Dialog__assistant" />
                    </Circle>
                )}

                {variant === 'alexa' && chatResponse && (
                    <Circle>
                        <AlexaSpeechBubble className="Dialog__assistant Dialog__assistant--alexa" />
                    </Circle>
                )}
            </div>

            {buttonText && (
                <Button className="Dialog__cta" variant="dialog" onClick={onDialogButtonClick}>
                    {buttonText}
                </Button>
            )}
        </div>
    );
};

Dialog.propTypes = propTypes;
Dialog.defaultProps = defaultProps;

export default Dialog;
